import {
  Button,
  Content,
  Flex,
  Heading,
  StrokeIcon,
  usePlatformInfo,
  usePlatformProps,
  useToast,
} from '@acsbe/ui';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { styled } from 'styled-components';
import giftVideo from '../../../assets/giftIcon.mp4';
import spinner from '../../../assets/spinner.gif';
import { useBilling } from '../../../context/BillingContext';
import { useDomainSelection } from '../../../context/DomainSelectionContext';
import { useMerchantSession } from '../../../context/MerchantSessionContext';
import { CMSFeedbackTriggerType } from '../../../type/UserFeedback';
import { MESSAGE_EVENT, sendMessageToWrapper } from '../../../utils';
import { checkValidExpiryTime } from '../../../utils/common';
import { CMS, CMS_CUSTOM_DISCOUNTS } from '../../../utils/constants';
import { Logger } from '../../../utils/logger';
import { BillingCycle } from './billing-cycle';
import { BillingCycle as Billing } from './Plan';
import { PlanBar } from './plan-bar';
import { Plans } from './Plans';
import { PlanType, PricingModel } from './types';

interface SubscriptionProps {
  isWidgetEnabled: boolean;
}

const ToastContent = (props: { message: string }) => {
  return (
    <Flex row paddingInline={4} paddingBlock={1.5} align="center" radius="xl">
      {props.message}
    </Flex>
  );
};

export const Subscription = (props: SubscriptionProps) => {
  const [plans, setPlans] = useState<PlanType[]>([]);
  const { uptoPhablet } = usePlatformInfo();
  const {
    accountId,
    licenseId,
    domain,
    licenseDetails,
    paymentOverlayMessage,
    refreshLicenseDetails,
    isCustomDiscount,
  } = useDomainSelection();
  const [params, setParams] = useSearchParams();
  const [expiryTime, setExpiryTime] = useState<number | null>(null);
  const [paymentStatusCheckInitiated, setPaymentStatusCheckInitiated] =
    useState(false);
  const { setWooPendingPayment } = useBilling();
  const {
    redirectUrl: sessionRedirectUrl,
    merchantDetails,
    setUserFeedback,
    willShowTrialStartedPopup,
  } = useMerchantSession();
  const redirectUrl =
    sessionRedirectUrl && sessionRedirectUrl.trim() !== ''
      ? sessionRedirectUrl
      : document.referrer;
  const toast = useToast();
  const p = usePlatformProps();

  useEffect(() => {
    const contractId = params.get('contract_id');
    const billingIntentId = params.get('billing_intent_id');
    const payment = params.get('payment');
    if (
      !contractId ||
      !billingIntentId ||
      !paymentStatusCheckInitiated ||
      !expiryTime ||
      payment !== 'woocom'
    ) {
      return;
    }

    const intervalId = setInterval(async () => {
      if (expiryTime < Date.now()) {
        clearInterval(intervalId);

        sendMessageToWrapper(MESSAGE_EVENT.REMOVE_QUERY_PARAM, {
          param: 'payment',
        });

        setParams((prev) => {
          prev.delete('payment');
          return prev;
        });

        toast(
          <ToastContent message="Your payment didn't go through within the expected time. Please try again." />,
          {
            scale: 'warning',
            duration: 10000,
            icon: 'Warning',
          },
        );

        refreshLicenseDetails();
        localStorage.removeItem('pollingTime');
        setWooPendingPayment(false);
        return;
      }

      const shouldStop = await pollWooCommPaymentStatus(
        contractId,
        billingIntentId,
        true,
      );

      if (shouldStop) {
        clearInterval(intervalId);
      }
    }, 5000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatusCheckInitiated, expiryTime, params]);

  const pollWooCommPaymentStatus = async (
    contractId: string,
    billingIntentId: string,
    forceSync: boolean = false,
  ) => {
    Logger.log('checking payment status from woocommerce', new Date());

    const response = await axios.post(
      `/api/accessibe/woo/check-subscription`,
      {
        contractId,
        billingIntentId,
        forceSync,
      },
      {
        withCredentials: true,
      },
    );
    const lastPaymentStatus =
      response.data?.subscription?.billingIntent?.status;
    if (lastPaymentStatus && lastPaymentStatus !== 'pending') {
      sendMessageToWrapper(MESSAGE_EVENT.REMOVE_QUERY_PARAM, {
        param: 'payment',
      });
      setParams((prev) => {
        prev.delete('payment');
        return prev;
      });
      const message =
        lastPaymentStatus == 'completed'
          ? 'Your payment was successful. Congrats on taking a big step to becoming accessible!'
          : "Your payment didn't go through. Please contact support for help.";
      toast(<ToastContent message={message} />, {
        scale: lastPaymentStatus === 'completed' ? 'success' : 'warning',
        duration: 10000,
        icon: lastPaymentStatus === 'completed' ? 'Check' : 'Warning',
      });
      refreshLicenseDetails();
      if (
        lastPaymentStatus === 'completed' &&
        domain &&
        merchantDetails?.source
      ) {
        setUserFeedback({
          cmsFeedbackTriggerType: CMSFeedbackTriggerType.POST_PURCHASE,
          pluginInstallSource: merchantDetails.source as CMS,
          siteUrl: domain,
        });
      }
      localStorage.removeItem('pollingTime');
      setWooPendingPayment(false);
      return true;
    }
    return false;
  };

  useEffect(() => {
    const payment = params.get('payment');
    const contractId = params.get('contract_id');
    const billingIntentId = params.get('billing_intent_id');
    if (
      !paymentStatusCheckInitiated &&
      payment &&
      payment === 'woocom' &&
      contractId &&
      billingIntentId
    ) {
      setPaymentStatusCheckInitiated(true);
      setWooPendingPayment(true);
      let pollingTime = localStorage.getItem('pollingTime');
      const PAYMENT_TIMEOUT_SECONDS = 600;
      if (
        pollingTime === null ||
        checkValidExpiryTime(pollingTime, 0, PAYMENT_TIMEOUT_SECONDS) === null
      ) {
        pollingTime = (Date.now() + 10 * 60 * 1000).toString();
        localStorage.setItem('pollingTime', pollingTime);
      }
      setExpiryTime(Number(pollingTime));
    } else {
      setWooPendingPayment(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (licenseDetails && licenseDetails.id !== '') {
      const plans = async () => {
        const response = await axios.get(
          `/api/accessibe/plans?pricingModel=${licenseDetails.pricingModel}&source=${merchantDetails?.source ?? 'unknown'}`,
          {
            withCredentials: true,
          },
        );
        let sortedPlans = response.data.data?.sort(
          (a: PlanType, b: PlanType) => a.limitation - b.limitation,
        );
        if (licenseDetails?.customPrice) {
          sortedPlans = sortedPlans.filter(
            (plan: PlanType) =>
              licenseDetails?.plan?.id == plan.id ||
              licenseDetails?.plan?.tier === plan?.tier,
          );
        }
        setPlans(sortedPlans);
      };
      plans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    licenseDetails?.id,
    licenseDetails?.pricingModel,
    licenseDetails?.customPrice,
    licenseDetails?.plan?.id,
    licenseDetails?.plan?.tier,
    merchantDetails?.source,
  ]);

  const [billingCycle, setBillingCycle] = useState<Billing>('annually');
  const [anuallyDiscountText, setAnnuallyDiscountText] = useState<string>('');

  useEffect(() => {
    if (licenseDetails?.monthly) {
      setBillingCycle('monthly');
    } else {
      setBillingCycle('annually');
    }
    if (licenseDetails?.pricingModel === PricingModel.visitCount) {
      setAnnuallyDiscountText('save 30%');
    } else {
      setAnnuallyDiscountText('save 20%');
    }
  }, [
    licenseDetails?.monthly,
    licenseDetails?.pricingModel,
    setAnnuallyDiscountText,
  ]);

  return (
    <Flex gap={6} paddingBlockStart={6} align="flex-start" stretch>
      <Heading
        level={2}
        size="xxs"
        emphasis="low"
        balance={false}
        {...p.uptoTablet({ style: { fontSize: 16 } })}
      >
        My Subscription
      </Heading>
      <PlanBar
        isWidgetEnabled={props.isWidgetEnabled}
        licenseDetails={licenseDetails}
      />
      <Heading
        level={2}
        size="xxs"
        emphasis="low"
        balance={false}
        {...p.uptoTablet({ style: { fontSize: 16 } })}
        id="accessibe-widget-choose-plan-header"
      >
        Choose the right plan for your store
      </Heading>
      {/* willShowTrialStartedPopup is used to render the animation here only */}
      {isCustomDiscount && !willShowTrialStartedPopup && (
        <AnimatedBorderContainer key={licenseDetails?.id}>
          <StyledFlex
            row
            scale="premium"
            variant="element"
            gap={4}
            paddingBlock={2}
            paddingInlineStart={2}
            border="xxs"
            paddingInlineEnd={6}
            justify="center"
            align="center"
            self="center"
          >
            <video
              src={giftVideo}
              width={40}
              height={40}
              autoPlay
              muted
              playsInline
              style={{ borderRadius: '9999999px' }}
            />
            <Content
              scale="base"
              size={uptoPhablet ? 'sm' : 'md'}
              emphasis="none"
              lineHeight={1.5}
            >
              Limited time offer: Up to{' '}
              <StyledSpan>
                {merchantDetails?.source &&
                  CMS_CUSTOM_DISCOUNTS[merchantDetails?.source]}
                % OFF
              </StyledSpan>{' '}
              on top of{' '}
              {licenseDetails?.pricingModel === 'visitsCount' ? '30%' : '20%'}{' '}
              yearly
            </Content>
          </StyledFlex>
        </AnimatedBorderContainer>
      )}
      <BorderFlex
        radius="xl"
        isCustomDiscount={isCustomDiscount}
        scale="ghost"
        padding={uptoPhablet ? 4 : 6}
        align="center"
        gap={6}
        self="stretch"
      >
        {paymentOverlayMessage && (
          <PaymentOverlay>
            <div>
              <img src={spinner} alt="Loading..." />
              <br />
              <span>{paymentOverlayMessage}</span>
            </div>
          </PaymentOverlay>
        )}
        <BillingCycle
          billingCycle={billingCycle}
          setBillingCycle={setBillingCycle}
          license={licenseDetails}
          annualDiscountText={anuallyDiscountText}
        />
        <Plans
          billingCycle={billingCycle}
          setBillingCycle={setBillingCycle}
          plans={plans}
          license={licenseDetails}
        />
        <Button
          scale="primary"
          variant="link"
          href={`${import.meta.env.VITE_DASHBOARD_URL}/app/widget-license/${licenseId}?switchAccount=${accountId}&type=accessWidget&redirect-url=${redirectUrl}`}
          target="_blank"
          data-mixpanel="compare-plans"
          style={{ opacity: paymentOverlayMessage ? '.1' : '1' }}
        >
          <span>Compare Plans</span>
          <StrokeIcon name="SquareUpRightStroke" />
        </Button>
      </BorderFlex>
    </Flex>
  );
};

const BorderFlex = styled(Flex)<{ isCustomDiscount: boolean }>`
  border: ${(props) =>
    props.isCustomDiscount ? '1px solid #7345EC' : '1px solid #e8ebf3'};
  position: relative;
`;

const PaymentOverlay = styled.div`
  top: 0;
  border-radius: 10px;
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: transparent;
  z-index: 1;

  div {
    img {
      width: 25px;
    }
    pre {
      font-family: 'SF Pro Text, Circular, sans-serif';
    }
    text-align: center;
    padding: 100px;
  }
`;

const StyledFlex = styled(Flex)`
  border-radius: inherit;
  background-color: transparent;
`;

const StyledSpan = styled.span`
  font-weight: 700;
  line-height: 125%;
`;

const AnimatedBorderContainer = styled.div`
  border-radius: 9999999px;
  z-index: 1;
  align-self: center;
  margin-bottom: -52px;

  background:
    linear-gradient(#fff, #fff) padding-box,
    conic-gradient(
        from var(--angle),
        #7345ec,
        transparent,
        transparent,
        #7345ec,
        #7345ec
      )
      border-box;

  border: 2px solid transparent;

  @property --angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }

  animation:
    spin 2s linear infinite,
    hideBorder 0s linear 3s forwards;

  display: inline-flex; /* prevents weird flex stretching */
  width: fit-content;

  @keyframes spin {
    from {
      --angle: 0deg;
    }
    to {
      --angle: 360deg;
    }
  }

  @keyframes hideBorder {
    to {
      background: #fff;
      border: 1px solid transparent;
    }
  }
`;
